/**
 * Cookie consent banner. Required under HHS 2024 Online Tracking Bulletin
 * for any healthcare site loading 3rd-party tracking pixels (PostHog, Sentry, etc).
 *
 * Behavior:
 *   • First page-view → banner appears at bottom. Until the user picks a
 *     choice, `analytics` is treated as DISABLED (PostHog is not initialised
 *     in `instrument.js` regardless — see hasAnalyticsConsent()).
 *   • User picks: Accept all / Reject non-essential / Customize → choice
 *     stored in localStorage AND posted to /api/consent/cookies (anonymous).
 *   • Banner re-appears if the cookies version bumps.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie, X, ShieldCheck } from "lucide-react";
import { useBottomRightStack } from "@/components/BottomRightStack";
import API from "@/lib/api";

const STORAGE_KEY = "glpflo_cookie_consent_v1";
export const CURRENT_COOKIES_VERSION = "2026.02";

export function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CURRENT_COOKIES_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent() {
  return !!readConsent()?.analytics;
}

export function hasMarketingConsent() {
  return !!readConsent()?.marketing;
}

function persistAndPost(choice) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(choice)); } catch { /* noop */ }
  try {
    API.post("/consent/cookies", {
      necessary: true,
      analytics: !!choice.analytics,
      marketing: !!choice.marketing,
      version: CURRENT_COOKIES_VERSION,
    }).catch(() => { /* anonymous endpoint may fail silently */ });
  } catch { /* noop */ }
  // Fire a window event so analytics modules can lazy-init when newly granted.
  try {
    window.dispatchEvent(new CustomEvent("glpflo:cookie-consent", { detail: choice }));
  } catch { /* noop */ }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const bannerRef = useRef(null);
  const [measuredH, setMeasuredH] = useState(0);

  // Register in the bottom-right stack so the chat (and any future
  // floating CTA) can lift above us. We don't apply the returned bottom
  // px ourselves — the banner stays anchored to `bottom-0` regardless.
  useBottomRightStack({
    id: "cookie-banner",
    priority: 10,
    visible: visible && measuredH > 0,
    height: measuredH,
  });

  useEffect(() => {
    setVisible(!readConsent());
  }, []);

  // Measure the rendered banner after every paint so the registered
  // height stays in sync with the compact / expanded "Customize" views.
  useLayoutEffect(() => {
    if (!visible || !bannerRef.current) {
      setMeasuredH(0);
      return undefined;
    }
    const measure = () => {
      const h = bannerRef.current?.getBoundingClientRect().height || 0;
      const rounded = Math.round(h);
      // GUARD: only update state when the height actually changed.
      // Without this, the ResizeObserver was firing on any layout recalc
      // (e.g. <html> data-theme attribute toggling) and calling setState
      // with the SAME value — which still schedules a re-render in React
      // and contributed to the "Maximum update depth exceeded" loop with
      // ThemeContext's 600 ms re-apply tick.
      setMeasuredH((prev) => (prev === rounded ? prev : rounded));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(bannerRef.current);
    return () => ro.disconnect();
  }, [visible, showCustom]);

  const accept = (choice) => {
    const final = {
      version: CURRENT_COOKIES_VERSION,
      necessary: true,
      analytics: !!choice.analytics,
      marketing: !!choice.marketing,
      decided_at: new Date().toISOString(),
    };
    persistAndPost(final);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      ref={bannerRef}
      className="fixed bottom-0 left-0 right-0 z-50 px-3 pb-3 sm:px-6 sm:pb-6 pointer-events-none"
      data-testid="cookie-banner"
    >
      <div className="mx-auto max-w-3xl pointer-events-auto rounded-2xl border border-slate-200 bg-white shadow-2xl dark:bg-slate-900 dark:border-slate-700">
        <div className="p-4 sm:p-5">
          {!showCustom ? (
            <>
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-9 h-9 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
                  <Cookie className="w-4.5 h-4.5 text-purple-600 dark:text-purple-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 dark:text-white text-sm">We respect your privacy.</p>
                  <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 leading-relaxed">
                    We use cookies for essential site functionality (always on),
                    and — only with your permission — for product analytics that
                    help us improve your experience. We do <b>not</b> use
                    advertising trackers and we never share Protected Health
                    Information with third-party trackers.
                    {" "}
                    <a href="/privacy" className="underline hover:text-purple-600">Privacy policy</a>
                    {" · "}
                    <a href="/online-tracking" className="underline hover:text-purple-600">Online tracking disclosure</a>
                  </p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-600 dark:text-slate-300"
                  onClick={() => setShowCustom(true)}
                  data-testid="cookie-customize-btn"
                >
                  Customize
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => accept({ analytics: false, marketing: false })}
                  data-testid="cookie-reject-btn"
                >
                  Reject non-essential
                </Button>
                <Button
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={() => accept({ analytics: true, marketing: false })}
                  data-testid="cookie-accept-btn"
                >
                  <ShieldCheck className="w-4 h-4 mr-1" /> Accept analytics
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-900 dark:text-white text-sm">Cookie preferences</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Pick which categories you allow. You can change this any time from the footer.</p>
                </div>
                <button
                  onClick={() => setShowCustom(false)}
                  className="text-slate-400 hover:text-slate-600"
                  aria-label="Close"
                ><X className="w-4 h-4" /></button>
              </div>
              <div className="mt-3 space-y-3">
                <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">Strictly necessary</p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">Login, security, fraud prevention. Always on — required for the site to work.</p>
                  </div>
                  <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">Always on</span>
                </div>
                <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">Product analytics</p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">Anonymous page-view counts. No PHI is captured. Helps us improve the product.</p>
                  </div>
                  <Switch
                    checked={analytics}
                    onCheckedChange={setAnalytics}
                    data-testid="cookie-toggle-analytics"
                  />
                </div>
                <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 dark:border-slate-700 p-3 opacity-60">
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">Marketing</p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">Not used. GLPFlo does not run advertising pixels.</p>
                  </div>
                  <Switch
                    checked={marketing}
                    onCheckedChange={setMarketing}
                    disabled
                    data-testid="cookie-toggle-marketing"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCustom(false)}
                >Back</Button>
                <Button
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={() => accept({ analytics, marketing })}
                  data-testid="cookie-save-custom-btn"
                >Save preferences</Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
